import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { BookOpen, Menu, X, Lock } from 'lucide-react';
import { useToast } from '../../lib/use-toast';
import { useAuth } from '../../lib/auth-context';
import { usePassCodeur } from '../../lib/pass-codeur-context';
import { getCourseByIdStatic } from '../../pages/cours/courses-data';


const STORAGE_KEY = 'courseProgress';

// Fonction pour obtenir le numéro du dernier chapitre complété
const getLastCompletedChapter = (courseId) => {
  try {
    const progress = localStorage.getItem(STORAGE_KEY);
    if (!progress) return -1; // Initialisation à -1 (rien n'est complété)
    
    const progressData = JSON.parse(progress);
    return progressData[courseId] ?? -1; // Utiliser -1 si pas de progression
  } catch (error) {
    console.error('Erreur lors de la lecture de la progression:', error);
    return -1;
  }
};



export const CourseSidebar = ({ courseStructure, courseId, isPurchased = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currentUser } = useAuth();
  const { hasAccessToCourses } = usePassCodeur();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [courseInfo, setCourseInfo] = useState(null);

  // Charger les informations du cours pour vérifier s'il est gratuit
  useEffect(() => {
    try {
      const course = getCourseByIdStatic(courseId);
      setCourseInfo(course);
    } catch (error) {
      console.error('Erreur lors du chargement des infos du cours:', error);
    }
  }, [courseId]);

  // Fonction pour vérifier l'accès au cours
  const hasAccessToCourse = () => {
    if (!courseInfo) return false;
    return courseInfo.isFree || hasAccessToCourses();
  };


  // Fermer le menu mobile lors d'un changement de route
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Détermine si le chapitre actuel est actif
  const isChapterActive = (chapterPath) => {
    return location.pathname === chapterPath || 
           (chapterPath !== courseStructure.chapters[0].path && location.pathname.startsWith(chapterPath));
  };

  // Détermine si un chapitre est complété
  const isChapterCompleted = (chapterId) => {
    const lastCompletedChapter = getLastCompletedChapter(courseId);
    
    // Cas spécial pour l'introduction
    if (chapterId === 'introduction') {
      return lastCompletedChapter >= 0;
    }
    
    // Pour les autres chapitres qui ont un format 'chapitreX'
    const chapterNumber = parseInt(chapterId.replace('chapitre', ''));
    return chapterNumber <= lastCompletedChapter;
  };

  const renderChapterItem = (chapter) => {
    const isActive = isChapterActive(chapter.path);
    const isCompleted = isChapterCompleted(chapter.id);
    const isIntroduction = chapter.id === 'introduction';
    
    // L'introduction est toujours accessible pour tous les cours
    // Les autres chapitres sont accessibles si l'utilisateur est connecté ET a accès au cours (Pass Codeur ou cours gratuit)
    const isAccessible = isIntroduction || (currentUser && hasAccessToCourse());

    // Si le chapitre n'est pas accessible, on utilise un div au lieu d'un Link
    const ChapterComponent = isAccessible ? Link : 'div';

    // Handler pour les chapitres non accessibles
    const handleRestrictedClick = () => {
      if (!isAccessible) {
        let message = "Accès restreint";
        let description = "Vous devez acheter le Pass Codeur pour accéder à ce chapitre.";
        
        if (!currentUser) {
          message = "Connexion requise";
          if (courseInfo?.isFree) {
            description = "Vous devez vous connecter pour accéder à ce chapitre.";
          } else {
            description = "Vous devez vous connecter et acheter le Pass Codeur pour accéder à ce chapitre.";
          }
        } else if (courseInfo?.isFree) {
          // L'utilisateur est connecté mais le cours est gratuit, donc ce message ne devrait pas apparaître
          message = "Erreur d'accès";
          description = "Une erreur est survenue lors de la vérification de l'accès.";
        }
        
        toast({
          title: message,
          description: description,
          variant: "destructive",
        });
      }
    };

    // Props communs
    const commonProps = {
      className: cn(
        "flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors",
            isActive 
              ? "bg-primary/5 hover:bg-primary/10" 
              : isCompleted
                ? "bg-green-50 hover:bg-green-100 dark:bg-green-950/30 dark:hover:bg-green-950/40"
            : !isAccessible
              ? "bg-muted/50 text-muted-foreground hover:bg-muted cursor-not-allowed"
                : "hover:bg-primary/5 text-foreground/80"
      ),
      ...(isAccessible 
        ? { to: chapter.path } 
        : { onClick: handleRestrictedClick, role: "button", "aria-disabled": "true" }
      )
    };

    return (
      <div key={chapter.id} className="space-y-1.5">
        <ChapterComponent {...commonProps}>
          <div className="flex items-center gap-2">
            {isAccessible ? (
            <BookOpen className={cn(
              "h-4 w-4",
              isCompleted
                ? "text-green-600 dark:text-green-400"
                : "text-primary"
            )} />
            ) : (
              <Lock className="h-4 w-4 text-muted-foreground" />
            )}
            <span className={cn(
              isCompleted && "text-green-700 dark:text-green-400",
              !isAccessible && "text-muted-foreground"
            )}>{chapter.title}</span>
          </div>
        </ChapterComponent>
      </div>
    );
  };



  return (
    <>


      {/* Bouton pour afficher/masquer la sidebar sur mobile */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="fixed bottom-6 right-6 bg-primary text-primary-foreground p-3 rounded-full z-50 md:hidden hover:scale-110 transition-all duration-200"
        aria-label="Toggle sidebar"
      >
        {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Sidebar pour desktop */}
      <aside className="fixed left-4 top-20 w-[250px] rounded-xl bg-background/80 border border-border/40 shadow-lg hidden md:block max-h-[calc(100vh-8rem)] overflow-y-auto">
        <div className="rounded-t-xl bg-background/90 border-b border-border/40 shadow-sm z-10">
          <div className="p-4 flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            <h2 className="font-medium">{courseStructure.title}</h2>
          </div>

        </div>
        
        <nav className="p-3">
          <div className="space-y-2">
            {courseStructure.chapters.map(chapter => renderChapterItem(chapter))}
          </div>
        </nav>
      </aside>

      {/* Sidebar mobile */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-200"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          
          <aside className="w-72 bg-background fixed top-0 left-0 z-50 shadow-xl transform transition-all duration-300 ease-out rounded-r-2xl max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border/40">
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-primary" />
                <h2 className="font-medium">{courseStructure.title}</h2>
              </div>
              <button 
                onClick={() => setMobileOpen(false)}
                className="p-1.5 rounded-full hover:bg-muted transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="p-3">
              <div className="space-y-2">
                {courseStructure.chapters.map(chapter => renderChapterItem(chapter))}
              </div>
            </nav>
          </aside>
        </div>
      )}
    </>
  );
}; 